<template>
  <div class="max-width">
    <v-row>
      <v-col cols="12" align="center" justify="center">
        <v-row justify="end">
          <v-col md="6">
            <h2 align="center" justify="center">Client-sitting</h2>
          </v-col>
          <v-col md="3" class="d-flex justify-end">
            <v-menu :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <v-checkbox
                  v-on="on"
                  readonly
                  hide-details
                  color="primary"
                  :indeterminate="hideCompleted.selectionCheckbox == null"
                  :value="hideCompleted.selectionCheckbox"
                  :label="hideCompleted.selectionText || defaultCompleteSelectionText"
                />
              </template>
              <v-card elevation="4" class="py-1 px-3">
                <v-treeview
                  dense
                  open-all
                  hoverable
                  selectable
                  selected-color="primary"
                  open-on-click
                  return-object
                  expand-icon="mdi-chevron-down"
                  v-model="hideCompleted.selection"
                  :items="hideCompleted.options"
                  @input="updateRows()"
                >
                </v-treeview>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
        <v-data-table
          height="490"
          :headers="table.headers"
          :options="table.options"
          :items="subscriptions"
          :item-class="rowClass"
          :search="search"
          :show-group-by="true"
          :hide-default-footer="true"
          disable-pagination
          fixed-header
          :loading="$apollo.loading"
          :custom-sort="customSort"
        >
          <template v-slot:top>
            <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
              <template v-slot:label>
                <v-icon color="secondary"> mdi-magnify </v-icon>
                Search
              </template>
            </v-text-field>
          </template>

          <template v-slot:[`item.profile_pic`]="props">
            <profile-pic :profilePic="props.item.profile_pic" :userId="props.item.userId" />
          </template>

          <template v-slot:[`header.client_name`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.client_name`]="props">
            <client-name
              :client-id="props.item.userId"
              :dob="props.item.dateOfBirth"
              :profileLink="props.item.profile_link"
              :subscriptionEndDate="props.item.subscriptionEndDate"
              :clientName="props.item.client_name"
              :username="props.item.username"
              :contactLink="props.item.contact_link"
              :lastActive="props.item.client.userProfile.lastActive"
              :coachingChat="props.item.client.userProfile.coachingChat"
              :supportChat="props.item.client.userProfile.supportChat"
              :coachId="coachId"
              :subscriptionSurveyId="props.item.subscriptionSurveyId"
            />
          </template>
          <template v-slot:[`header.comms`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.comms`]="props">
            <comms
              :item="props.item"
              @update-am="updateCheckbox(props.item)"
              @update-pm="updateCheckbox(props.item, (day_half = 'Afternoon'))"
            />
          </template>

          <template v-slot:[`item.controls`]="props" width="100">
            <tool-buttons
              :coachId="coachId"
              :userId="props.item.userId"
              :username="props.item.username"
              :clientName="props.item.client_name"
              :activityLoggedToday="props.item.activity_logged_today"
              class="text-center mb-2 mt-1"
            />
            <client-week-dots :userId="props.item.userId" show-arrows />
          </template>

          <template v-slot:[`header.activeWorkoutBlocks`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.activeWorkoutBlocks`]="props">
            <active-workout-blocks :workoutBlocks="props.item.activeWorkoutBlocks" :nextWorkoutBlocks="props.item.nextWorkoutBlocks" />
          </template>

          <template v-slot:[`header.nutritionPlan`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.nutritionPlan`]="props">
            <span class="block caption">
              <b>Plan:</b>
              <a :href="props.item.nutritionPlan.pdf" target="_blank">{{ props.item.nutritionPlan.title }}</a> </span
            ><br />
            <span class="block caption"> <b>Block:</b> {{ props.item.nutritionBlock }} </span>
          </template>

          <template v-slot:[`header.lastMessage`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastMessage`]="props">
            <last-message-column @viewed-message="onMessageViewed" :message="props.item.lastMessage" />
          </template>
          <template v-slot:[`header.resolved`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.resolved`]="props">
            <resolve-message-btn :chat="props.item.chat" :resolved="props.item.resolved" @reload="reloadData" />
          </template>

          <template v-slot:[`header.lastCoachingAssessmentDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastCoachingAssessmentDate`]="props">
            <span
              v-if="props.item.lastCoachingAssessmentDate"
              :class="getAssessmentClass(props.item.lastCoachingAssessmentDate)"
              >{{ formatAssessmentDate(props.item.lastCoachingAssessmentDate) }}</span
            >
            <span v-else class="red--text">No check in!</span>
          </template>

          <template v-slot:[`header.lastUserAssessmentDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastUserAssessmentDate`]="props">
            <span
              v-if="props.item.lastUserAssessmentDate"
              :class="getAssessmentClass(props.item.lastUserAssessmentDate)"
              >{{ formatAssessmentDate(props.item.lastUserAssessmentDate) }}</span
            >
            <span v-else class="red--text">No check in!</span>
          </template>
          <template v-slot:[`header.weeksOnProgram`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>

          <template v-slot:[`item.engagementScore`]="props">
            <engagement-score-circle :engagement-score="props.item.engagementScore" />
          </template>

          <template v-slot:[`header.has_goal`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.has_goal`]="props">
            <bhag :hasGoal="props.item.has_goal" />
          </template>
          <template v-slot:[`header.has_checkin_goal`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.has_checkin_goal`]="props">
            <checkin-goal :hasGoal="props.item.has_checkin_goal" :agedGoal="props.item.has_aged_checkin_goal" />
          </template>

          <template v-slot:[`item.productType`]="props">
            {{props.item.productType}}
          </template>

        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CoachingDashboardBase from "../mixins/CoachingDashboardBase";
import OneOnOneDashboardBase from "../mixins/OneOnOneDashboardBase";

export default {
  name: "CoachingSecondary",
  props: ["coachId"],
  mixins: [CoachingDashboardBase, OneOnOneDashboardBase],
  data() {
    return {
      skipDashboardListingQuery: true,
      secondaryCoachId: btoa("UserNode:" + this.coachId),
      subscriptions: [],
      search: "",
      defaultCompleteSelectionText: "Hide rows",
      selectedUsername: "",
      selectedFullname: "",
      queryProgramType: "",
      header: "",
      modals: {
        userProgram: false,
        trainingLog: false,
        nutritionLog: false,
      },
      table: {
        options: {
          showGroupBy: true,
          multiSort: true,
          groupBy: ["covering"],
        },
        headers: [
          { text: "", value: "profile_pic", groupable: false },
          { text: "Client", value: "client_name", groupable: false },
          { text: "Product", value: "productType", groupable: false },
          { text: "Covering", value: "covering", groupable: true, width: 165 },
          {
            text: "Daily comms",
            value: "comms",
            align: "center",
            sortable: true,
            groupable: false,
            help: "Client communications AM / PM ",
          },
        ],
      },
    };
  },
  methods: {
    showTrainingLog(username, fullname) {
      this.setSelected(username, fullname);
      this.modals.trainingLog = true;
    },
    showNutritionLog(username, fullname) {
      this.setSelected(username, fullname);
      this.modals.nutritionLog = true;
    },
    showUserProgram(userId, username, fullname) {
      this.setSelected(username, fullname);
      this.selectedUserId = userId;
      this.modals.userProgram = true;
    },
    setSelected(username, fullname) {
      this.selectedFullname = fullname;
      this.selectedUsername = username;
    },
    closeUserProgram() {
      this.modals.userProgram = false;
    },
  },
};
</script>

<style scoped>
.max-width {
  width: 100%;
}

.exercise-dot {
  position: absolute;
  top: 16px;
  background-color: var(--v-exercise-base);
  border: 1px solid white;
  border-radius: 100%;
  width: 22px;
  height: 22px;
}

.limited-height {
  height: 650px;
}
</style>
